import { useEffect, useRef, useState, type MouseEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { useReducedMotion } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import LangToggle from "@/components/lang-toggle";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
import { SheetModeToggle } from "./SheetModeToggle";

/*
  The sheet's nav and footer. The footer is the last sheet: ink by day, bone
  by night. The shared LangToggle sits in both unchanged: it reads the app's
  tokens, which this world re-points (sheet.css); the day-or-night toggle sits
  beside it, cut the same way.

  The nav is a strip of paper pinned to the top of the page. Its middle names
  the three rooms, each with a chip of its own colour, then About and FAQ; on
  the landing the room links scroll to the room's own section, and the one
  you are reading is marked in its colour, both under the link and along the
  nav's bottom edge. Once both of the page's plates have left the screen, the
  plate comes to the nav, so signing up is always one tap away; it trades
  places with Sign in on a phone, in a slot that never changes width, so
  nothing in the nav moves when it does.

  Below 1024px the links fold into a menu: the three rooms as whole bands of
  their colours, the way the page lays them out, then About, FAQ, Sign in,
  the locale, day or night, and the plate.
*/

const ROOMS = ["string", "knot", "loop"] as const;
type Room = (typeof ROOMS)[number];

/** The room section whose band is under the middle of the screen, on the landing. */
function useActiveRoom(enabled: boolean): Room | null {
  const [active, setActive] = useState<Room | null>(null);
  useEffect(() => {
    if (!enabled || typeof IntersectionObserver === "undefined") return;
    const seen = new Map<Room, boolean>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          seen.set(entry.target.id.replace(/s$/, "") as Room, entry.isIntersecting);
        }
        setActive(ROOMS.find((room) => seen.get(room)) ?? null);
      },
      /* A thin line just above the middle of the viewport. */
      { rootMargin: "-42% 0px -56% 0px" }
    );
    ROOMS.forEach((room) => {
      const el = document.getElementById(`${room}s`);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [enabled]);
  return active;
}

/** True once the page has moved, and once none of its own plates are on screen. */
function useNavState(home: boolean) {
  const [scrolled, setScrolled] = useState(false);
  const [plateAway, setPlateAway] = useState(!home);

  useEffect(() => {
    let frame = 0;
    const read = () => {
      frame = 0;
      setScrolled(window.scrollY > 4);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(read);
    };
    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    if (!home || typeof IntersectionObserver === "undefined") return;
    const plates = Array.from(document.querySelectorAll("[data-cta]"));
    if (plates.length === 0) return;
    const showing = new Set<Element>();
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) showing.add(entry.target);
        else showing.delete(entry.target);
      }
      setPlateAway(showing.size === 0 && window.scrollY > 0);
    });
    plates.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [home]);

  return { scrolled, plateAway };
}

export function SheetNav() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const still = useReducedMotion();
  const home = pathname === "/";
  const active = useActiveRoom(home);
  const { scrolled, plateAway } = useNavState(home);
  const [open, setOpen] = useState(false);
  const header = useRef<HTMLElement | null>(null);
  /* The menu portals into this page's `.sheet` root, not <body>, so it keeps
     the world's tokens. */
  const [root, setRoot] = useState<HTMLElement | null>(null);
  useEffect(() => setRoot(header.current?.closest<HTMLElement>(".sheet") ?? null), []);

  /* On the landing a room link scrolls to its section (smoothly, unless motion
     is reduced) and leaves the hash behind; elsewhere it is a link to it. */
  const go = (room: Room) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (!home) return;
    const target = document.getElementById(`${room}s`);
    if (!target) return;
    event.preventDefault();
    setOpen(false);
    /* After the menu has let go of the page's scroll. */
    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: still ? "auto" : "smooth", block: "start" });
      history.replaceState(null, "", `#${room}s`);
    });
  };
  const roomHref = (room: Room) => (home ? `#${room}s` : `/#${room}s`);
  /* On the landing the wordmark goes back to the top (and the thread pulls back up with it). */
  const top = (event: MouseEvent<HTMLAnchorElement>) => {
    setOpen(false);
    if (!home) return;
    event.preventDefault();
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: still ? "auto" : "smooth" });
      history.replaceState(null, "", "/");
    });
  };

  return (
    <header
      ref={header}
      className={cn("sh-nav", scrolled && "is-scrolled")}
      data-room={active ?? undefined}
    >
      <Link to="/" className="sh-wordmark" aria-label={t("landing.home")} onClick={top}>
        aSTRING
      </Link>

      <nav className="sh-nav__middle" aria-label={t("nav.aria")}>
        {ROOMS.map((room) => (
          <a
            key={room}
            href={roomHref(room)}
            onClick={go(room)}
            className={cn("sh-nav__room", `sh-nav__room--${room}`, active === room && "is-active")}
            aria-current={active === room ? "location" : undefined}
          >
            <span className="sh-nav__chip" aria-hidden />
            {t(`rooms.${room}.plural`)}
          </a>
        ))}
        <span className="sh-nav__gap" aria-hidden />
        <Link to="/about" className="sh-nav__link" aria-current={pathname === "/about" ? "page" : undefined}>
          {t("nav.about")}
        </Link>
        <Link to="/faq" className="sh-nav__link" aria-current={pathname === "/faq" ? "page" : undefined}>
          {t("nav.faq")}
        </Link>
      </nav>

      <div className="sh-nav__end">
        {/* One slot, the width of the wider of its two children, so the swap
            between Sign in and the plate never moves anything else. */}
        <span className="sh-nav__swap">
          <Link
            to="/signup"
            className={cn("sh-plate sh-nav__plate", plateAway && "is-shown")}
            aria-hidden={!plateAway || undefined}
            tabIndex={plateAway ? undefined : -1}
          >
            {t("hero.cta")}
          </Link>
          <Link
            to="/login"
            className={cn("sh-nav__link sh-nav__signin--phone", plateAway && "is-away")}
            aria-hidden={plateAway || undefined}
            tabIndex={plateAway ? -1 : undefined}
          >
            {t("nav.signIn")}
          </Link>
        </span>
        <Link to="/login" className="sh-nav__link sh-nav__signin">
          {t("nav.signIn")}
        </Link>
        <LangToggle className="sh-nav__lang" />
        <SheetModeToggle className="sh-nav__lang" />

        <Dialog.Root open={open} onOpenChange={setOpen}>
          <Dialog.Trigger className="sh-nav__menu" aria-label={t("nav.menu")}>
            <Icon glyph={Menu} scale="control" />
          </Dialog.Trigger>
          <Dialog.Portal container={root ?? undefined}>
            <Dialog.Content className="sh-menu" aria-describedby={undefined}>
              <Dialog.Title className="sr-only">{t("nav.menu")}</Dialog.Title>
              <div className="sh-menu__bar">
                <Link to="/" className="sh-wordmark" onClick={top}>
                  aSTRING
                </Link>
                <Dialog.Close className="sh-nav__menu" aria-label={t("nav.close")}>
                  <Icon glyph={X} scale="control" />
                </Dialog.Close>
              </div>

              <nav className="sh-menu__rooms" aria-label={t("nav.aria")}>
                {ROOMS.map((room) => (
                  <a
                    key={room}
                    href={roomHref(room)}
                    onClick={go(room)}
                    className={cn("sh-menu__room", `sh-field--${room}`)}
                    aria-current={active === room ? "location" : undefined}
                  >
                    <span className="sh-menu__word">{t(`rooms.${room}.plural`)}</span>
                    <span className="sh-menu__gloss">{t(`rooms.compare.gloss.${room}`)}</span>
                    <Icon glyph={ArrowRight} scale="control" className="sh-menu__arrow" />
                  </a>
                ))}
              </nav>

              <div className="sh-menu__pages">
                <Link to="/about" onClick={() => setOpen(false)}>
                  {t("nav.about")}
                </Link>
                <Link to="/faq" onClick={() => setOpen(false)}>
                  {t("nav.faq")}
                </Link>
                <Link to="/login" onClick={() => setOpen(false)}>
                  {t("nav.signIn")}
                </Link>
              </div>

              <div className="sh-menu__foot">
                <Link to="/signup" className="sh-plate" onClick={() => setOpen(false)}>
                  {t("hero.cta")}
                </Link>
                <div className="sh-prefs">
                  <LangToggle />
                  <SheetModeToggle />
                </div>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </header>
  );
}

const DOCS = "https://docs.astring.app/policies";

export function SheetFooter() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const still = useReducedMotion();
  const year = new Date().getFullYear();
  /* On the landing the footer's mark goes back to the top, the way the nav's does. */
  const top = (event: MouseEvent<HTMLAnchorElement>) => {
    if (pathname !== "/") return;
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: still ? "auto" : "smooth" });
    history.replaceState(null, "", "/");
  };
  return (
    <footer className="sh-footer">
      <div className="sh-wrap">
        <Link to="/" className="sh-footer__mark" aria-label={t("landing.home")} onClick={top}>
          aSTRING
        </Link>
        <div className="sh-footer__row">
          <ul className="sh-footer__links">
            <li>
              <Link to="/login">{t("nav.signIn")}</Link>
            </li>
            <li>
              <Link to="/about">{t("footer.links.about")}</Link>
            </li>
            <li>
              <Link to="/faq">{t("footer.links.faq")}</Link>
            </li>
            <li>
              <a href="mailto:yep@astring.app">{t("footer.links.contact")}</a>
            </li>
            <li>
              <a href={`${DOCS}/privacy-policy`} target="_blank" rel="noreferrer">
                {t("footer.links.privacy")}
              </a>
            </li>
            <li>
              <a href={`${DOCS}/terms-and-conditions`} target="_blank" rel="noreferrer">
                {t("footer.links.terms")}
              </a>
            </li>
          </ul>
          <div className="sh-prefs">
            <LangToggle />
            <SheetModeToggle />
          </div>
        </div>
        <p className="sh-footer__copy">{t("footer.copyright", { year })}</p>
      </div>
    </footer>
  );
}
